import { zodResolver } from '@hookform/resolvers/zod'
import { BuildingsIcon, PlusIcon } from '@phosphor-icons/react'
import { useEffect, useMemo } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useCreateBuilding } from '@/api/building'
import {
  AvatarFormField,
  NumericInputFormField,
  PhoneNumberFormField,
  SelectFormField,
} from '@/components/form'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Button } from '@/components/ui/button'
import { Grid } from '@/components/ui/grid'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { SelectOption } from '@/components/ui/select'
import { routerFactory, toastFactory } from '@/factories'
import { useClearStateOnCountryChange, useDirtyFormGuard, useStateOptions } from '@/hooks'
import { normalizeZipCode } from '@/pages/clients/client-profile/general-information/general-information.utils'
import { getApiErrorMessage, getApiErrorStatus } from '@/utils/error'
import { normalizeLink } from '@/utils/link'

import { FormChangeFields, getFormChanges } from '../../shared/form-changes'
import { PersonFormLayout } from '../../shared/person-form-layout'
import { getPhoneDigits } from '../../shared/person-table.utils'
import { mapPlaceSaveError, PlaceSaveErrorKind } from '../features/places-features.errors'
import { PLACE_TIMEZONES } from '../places.timezones'
import { toBuildingPhone } from '../places.utils'
import {
  makePlaceFormSchema,
  PlaceCountry,
  PlaceFormData,
  placeFormDefaultValues,
} from './place-form-modal.constants'

interface PlaceFormModalProps {
  onClose: VoidFunction
}

export const PlaceFormModal = ({ onClose }: PlaceFormModalProps) => {
  const { t } = useTranslation(['organisation', 'common'])
  const { navigate } = routerFactory.useRouter()
  const { mutateAsync: createBuilding } = useCreateBuilding()

  const schema = useMemo(() => makePlaceFormSchema(t), [t])

  const countryOptions = useMemo<SelectOption<PlaceCountry>[]>(
    () => [
      { label: t('organisation:places.create.country.us'), value: PlaceCountry.US },
      { label: t('organisation:places.create.country.ca'), value: PlaceCountry.CA },
    ],
    [t],
  )

  const timezoneOptions = useMemo<SelectOption<string>[]>(
    () => [
      { label: t('organisation:places.building.timezone.options.pt'), value: PLACE_TIMEZONES.PT },
      { label: t('organisation:places.building.timezone.options.mt'), value: PLACE_TIMEZONES.MT },
      { label: t('organisation:places.building.timezone.options.az'), value: PLACE_TIMEZONES.AZ },
      { label: t('organisation:places.building.timezone.options.ct'), value: PLACE_TIMEZONES.CT },
      { label: t('organisation:places.building.timezone.options.et'), value: PLACE_TIMEZONES.ET },
    ],
    [t],
  )

  const form = useForm<PlaceFormData>({
    defaultValues: placeFormDefaultValues,
    mode: 'onTouched',
    resolver: zodResolver(schema),
  })

  const { isDirty, isSubmitting } = form.formState
  const country = form.watch('country')
  const stateOptions = useStateOptions(country)
  const isCanada = country === PlaceCountry.CA

  useClearStateOnCountryChange(country, () => form.setValue('state', ''))

  // Note: the zip / postal code rule depends on the country, so a filled-in value is checked again when it changes
  useEffect(() => {
    if (form.getValues('postalCode')) form.trigger('postalCode')
  }, [country, form])

  const changeFields = useMemo<FormChangeFields<PlaceFormData>>(
    () => ({
      address: { label: t('organisation:places.building.fields.address.label') },
      city: { label: t('organisation:places.building.fields.city.label') },
      code: { label: t('organisation:places.building.fields.code.label') },
      country: {
        format: (value) =>
          countryOptions.find((option) => option.value === value)?.label ?? String(value),
        label: t('organisation:places.create.country.label'),
      },
      email: { label: t('organisation:places.building.fields.email.label') },
      link: { label: t('organisation:places.building.fields.link.label') },
      logo: { image: true, label: t('organisation:places.building.logotype') },
      name: { label: t('organisation:places.building.fields.name.label') },
      phone: { label: t('organisation:places.building.fields.phone.label') },
      postalCode: { label: t('organisation:places.building.fields.postalCode.label') },
      state: { label: t('organisation:places.building.fields.state.label') },
      timezone: {
        format: (value) =>
          timezoneOptions.find((option) => option.value === value)?.label ?? String(value),
        label: t('organisation:places.building.timezone.label'),
      },
    }),
    [t, countryOptions, timezoneOptions],
  )

  const guard = useDirtyFormGuard(
    isDirty && !isSubmitting,
    () =>
      getFormChanges({
        defaultValues: form.formState.defaultValues,
        fields: changeFields,
        values: form.getValues(),
      }),
    { inPopup: true },
  )

  const handleClose = () => {
    guard(onClose)
    return false
  }

  const onSubmit: SubmitHandler<PlaceFormData> = async (data) => {
    try {
      const building = await createBuilding({
        address: data.address.trim(),
        city: data.city?.trim() || undefined,
        code: data.code.trim(),
        country: data.country,
        email: data.email?.trim() || undefined,
        link: data.link?.trim() ? normalizeLink(data.link) : undefined,
        logo: data.logo?.[0],
        // Note: the backend defaults it to true, the legacy form created buildings with it off
        manualApproval: false,
        name: data.name.trim(),
        phone: toBuildingPhone(getPhoneDigits(data.phone)),
        postalCode: normalizeZipCode(data.postalCode.trim()),
        state: data.state?.trim() || undefined,
        timezone: data.timezone,
      })
      toastFactory.success(t('organisation:places.building.toasts.created'))
      onClose()
      navigate({
        params: { buildingId: String(building.id) },
        path: '/dashboard/organisation/places/:buildingId',
        queryParams: { tab: 'general' },
      })
    } catch (error) {
      const message = getApiErrorMessage(error)
      const { kind } = mapPlaceSaveError(message, getApiErrorStatus(error as Error))

      if (kind === PlaceSaveErrorKind.codeExists) {
        form.setError('code', { message: t('organisation:places.building.errors.codeExists') })
        return
      }
      toastFactory.error(message)
    }
  }

  const submit = form.handleSubmit(onSubmit)

  return (
    <Modal
      autoWidth
      title={t('organisation:places.create.title')}
      headerIcon={BuildingsIcon}
      footer={
        <>
          <Button use="secondary" onClick={handleClose}>
            {t('common:actions.back')}
          </Button>
          <Button loading={isSubmitting} use="primary" onClick={submit}>
            {t('common:actions.add')}
            <Icon icon={PlusIcon} />
          </Button>
        </>
      }
      onBeforeClose={handleClose}
      onClose={onClose}
    >
      <PersonFormLayout form={form} onSubmit={submit}>
        <AvatarFormField
          label={t('organisation:places.building.logotype')}
          name="logo"
          uploadLabel={t('organisation:places.create.logo.uploadLabel')}
        />

        <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
          <TextInputFormField
            withReservedFooter
            required
            label={t('organisation:places.building.fields.name.label')}
            name="name"
            placeholder={t('organisation:places.building.fields.name.placeholder')}
          />
          <NumericInputFormField
            withReservedFooter
            required
            label={t('organisation:places.building.fields.code.label')}
            name="code"
            placeholder={t('organisation:places.building.fields.code.placeholder')}
          />
        </Grid>

        <TextInputFormField
          withReservedFooter
          required
          label={t('organisation:places.building.fields.address.label')}
          name="address"
          placeholder={t('organisation:places.building.fields.address.placeholder')}
        />

        <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
          <SelectFormField<PlaceFormData, PlaceCountry>
            withReservedFooter
            required
            label={t('organisation:places.create.country.label')}
            name="country"
            options={countryOptions}
            size="medium"
            type="single"
          />
          <TextInputFormField
            withReservedFooter
            label={t('organisation:places.building.fields.city.label')}
            name="city"
            placeholder={t('organisation:places.building.fields.city.placeholder')}
          />
        </Grid>

        <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
          <SelectFormField<PlaceFormData, string>
            withReservedFooter
            searchable
            label={t('organisation:places.building.fields.state.label')}
            name="state"
            options={stateOptions}
            placeholder={t(
              isCanada
                ? 'organisation:places.building.fields.state.placeholderCA'
                : 'organisation:places.building.fields.state.placeholder',
            )}
            size="medium"
            type="single"
            onReset={() => form.setValue('state', '', { shouldDirty: true })}
          />
          <TextInputFormField
            withReservedFooter
            required
            label={t('organisation:places.building.fields.postalCode.label')}
            name="postalCode"
            placeholder={t(
              isCanada
                ? 'organisation:places.building.fields.postalCode.placeholderCA'
                : 'organisation:places.building.fields.postalCode.placeholder',
            )}
          />
        </Grid>

        <SelectFormField<PlaceFormData, string>
          withReservedFooter
          required
          label={t('organisation:places.building.timezone.label')}
          name="timezone"
          options={timezoneOptions}
          placeholder={t('organisation:places.building.timezone.placeholder')}
          size="medium"
          type="single"
        />

        <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
          <TextInputFormField
            withReservedFooter
            autoComplete="off"
            label={t('organisation:places.building.fields.email.label')}
            name="email"
            placeholder={t('organisation:places.building.fields.email.placeholder')}
          />
          <PhoneNumberFormField
            withReservedFooter
            required
            label={t('organisation:places.building.fields.phone.label')}
            name="phone"
            placeholder={t('organisation:places.building.fields.phone.placeholder')}
            size="medium"
          />
        </Grid>

        <TextInputFormField
          withReservedFooter
          label={t('organisation:places.building.fields.link.label')}
          name="link"
          placeholder={t('organisation:places.building.fields.link.placeholder')}
        />
      </PersonFormLayout>
    </Modal>
  )
}
